import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { tap } from 'rxjs/operators';
import { SafeUrlPipeModule } from '../../../pipes/safe-url.pipe';
import { ActivityPlayerEvent } from '../../activity-player/activity-player-event.enum';
import { isIFrameSameOrigin } from '../../helpers/is-same-origin';
import { ImmersionCourseService } from '../../immersion-course.service';
import { LessonPlayerService } from '../../lesson-player.service';
import { OeAppService } from '../../oe-app.service';
import { StudyBundleCourseDto } from '../../types/StudyBundleCourseDto';
import { UserwaySyncModule } from '../../userway-sync.directive';

@Component({
  selector: 'lp2-web-isc-player',
  templateUrl: './web-isc-player.component.html',
  styleUrls: ['./web-isc-player.component.scss'],
})
export class WebIscPlayerComponent implements OnInit, OnDestroy {
  @Input() course: StudyBundleCourseDto = {} as StudyBundleCourseDto;
  @Input() complete = false;
  @Output() started = new EventEmitter();
  @Output() completeChange = new EventEmitter<boolean>();
  @Output() close = new EventEmitter();
  @ViewChild('iframe') iframe: ElementRef | null = null;
  playerUrl: string = '';

  constructor(
    private readonly oeApp: OeAppService,
    private readonly immersionCourseService: ImmersionCourseService,
    private readonly lessonPlayerService: LessonPlayerService
  ) {
    this.handlePlayerEvents = this.handlePlayerEvents.bind(this);
    this.toggleIndex = this.toggleIndex.bind(this);
  }

  ngOnInit() {
    if (!this.course.id) {
      return;
    }

    if (this.complete) {
      this.setPlayerUrl$().subscribe(() => {
        this.started.emit();
      });
    } else {
      this.immersionCourseService.retrieveBookmark(this.course.id).subscribe((userBookmark) => {
        this.setPlayerUrl$(userBookmark).subscribe(() => {
          this.started.emit();
        });
      });
    }

    window.addEventListener('message', this.handlePlayerEvents, false);
  }

  setPlayerUrl$(bookmark = 0) {
    return this.lessonPlayerService
      .getIscPlayerUrl$(this.course!, bookmark)
      .pipe(tap((playerUrl) => (this.playerUrl = playerUrl)));
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.handlePlayerEvents);
  }

  updateBookmark(contentId: number, slideIndex: number) {
    this.immersionCourseService.saveBookmark(contentId, slideIndex).subscribe();
  }

  toggleIndex() {
    this.postMessage(ActivityPlayerEvent.TOGGLE_INDEX);
  }

  sendLessonInfo() {
    this.postMessage({
      type: ActivityPlayerEvent.SEND_LESSON_INFO,
      value: {
        id: this.course.id,
        title: this.course.title,
        description: this.course.description,
        lessonId: this.course.mediaData,
        contentType: this.course.contentType,
      },
    });
  }

  handlePlayerEvents({ data, origin }) {
    if (!origin || !this.oeApp.webIscPlayerHost || !isIFrameSameOrigin(this.oeApp.webIscPlayerHost, origin)) {
      return;
    }

    if (!data || !data.type) {
      return;
    }

    const type: ActivityPlayerEvent = data.type;

    switch (type) {
      case ActivityPlayerEvent.REQUEST_LESSON_INFO:
        this.sendLessonInfo();
        break;
      case ActivityPlayerEvent.ACTIVITY_COMPLETE:
        this.updateBookmark(this.course.id, data.index + 1);
        break;
      case ActivityPlayerEvent.LESSON_COMPLETE:
        this.completeChange.emit(true);
        break;
      case ActivityPlayerEvent.FINISH_LESSON:
        this.close.emit();
        break;
    }
  }

  private postMessage(payload: any) {
    if (this.iframe && this.iframe.nativeElement.contentWindow) {
      this.iframe.nativeElement.contentWindow.postMessage(payload, '*');
    }
  }
}

@NgModule({
  imports: [CommonModule, SafeUrlPipeModule, UserwaySyncModule],
  declarations: [WebIscPlayerComponent],
  exports: [WebIscPlayerComponent],
})
export class WebIscPlayerModule {}



// WEBPACK FOOTER //
// ./ts/app/shared/industry-content-modal/web-isc-player/web-isc-player.component.ts